import * as signalR from "@microsoft/signalr";
import { debugLog, debugWarn, debugError } from "../common/utils/debug";
import { AvatarSettings } from "../types";
import { azureCustomAvatarService } from "./azure-custom-avatar.service";
import {
  SIGNALR_HUB_METHODS,
  SOCKET_EVENTS,
  SIGNALR_RECONNECT_BASE_DELAY_MS,
  SIGNALR_RECONNECT_MAX_DELAY_MS,
} from "../common/constants/app.constants";

/**
 * SignalR service for communication with the server
 * ! Starts the events from the server
 */
class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private serverUrl = process.env.REACT_APP_SIGNALR_URL!;
  private currentUserId: string | null = null;
  private currentSessionId: string | null = null;
  private avatarSettings: AvatarSettings = {};
  private reconnectAttempts = 0;
  private hasLoggedFirstChunk = false;
  private intentionalDisconnect = false;
  private isPaused = false;

  async connect(
    userId: string,
    avatarSettings: AvatarSettings,
    interviewId: string
  ): Promise<signalR.HubConnection> {
    this.avatarSettings = avatarSettings || {};
    this.currentUserId = userId;

    if (this.connection) {
      await this.disconnect();
    }

    return new Promise((resolve, reject) => {
      try {
        // Build retry delays array: 0, 1s, 2s, 4s, 8s, 16s, 30s, 30s, 30s...
        const retryDelays = [0]; // First retry immediately
        let delay = SIGNALR_RECONNECT_BASE_DELAY_MS;
        for (let i = 0; i < 10; i++) {
          retryDelays.push(Math.min(delay, SIGNALR_RECONNECT_MAX_DELAY_MS));
          delay *= 2;
        }

        this.connection = new signalR.HubConnectionBuilder()
          .withUrl(this.serverUrl, {
            skipNegotiation: false,
            transport: signalR.HttpTransportType.WebSockets,
          })
          .withAutomaticReconnect(retryDelays)
          .configureLogging(signalR.LogLevel.Information)
          .build();

        this.setupServerEventHandlers();

        this.connection
          .start()
          .then(() => {
            this.reconnectAttempts = 0;
            return this.startSession(interviewId, userId, avatarSettings);
          })
          .then(() => {
            resolve(this.connection!);
          })
          .catch((error: any) => {
            reject(error);
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  private setupServerEventHandlers(): void {
    if (!this.connection) return;

    this.connection.onclose((error: any) => {
      this.handleDisconnection();
    });

    this.connection.onreconnecting((error: any) => {
      this.reconnectAttempts++;
      debugLog(`🔄 [SignalR] Reconnecting... Attempt ${this.reconnectAttempts}`, error?.message || '');
      this.emit(SOCKET_EVENTS.RECONNECTING, {
        attempt: this.reconnectAttempts,
        error: error?.message,
        timestamp: new Date()
      });
    });

    this.connection.onreconnected((connectionId: string | undefined) => {
      debugLog(`✅ [SignalR] Reconnected successfully. ConnectionId: ${connectionId}`);
      this.reconnectAttempts = 0;
      this.emit(SOCKET_EVENTS.RECONNECTED, {
        connectionId,
        timestamp: new Date()
      });
    });

    this.connection.on(SOCKET_EVENTS.SESSION_STARTED, (data: any) => {
      if (data?.sessionId) {
        this.currentSessionId = data.sessionId;
      } else {
        this.currentSessionId = null;
      }
      this.emit(SOCKET_EVENTS.SESSION_STARTED, data);
    });

    this.connection.on(SOCKET_EVENTS.SESSION_ERROR, (data: any) => {
      this.emit(SOCKET_EVENTS.SESSION_ERROR, data);
    });

    this.connection.on(SOCKET_EVENTS.USER_MESSAGE_TEXT, (data: any) => {
      this.emit(SOCKET_EVENTS.USER_MESSAGE_TEXT, data);
    });

    this.connection.on(SOCKET_EVENTS.RESPONSE_TEXT, (data: any) => {
      this.emit(SOCKET_EVENTS.RESPONSE_TEXT, data);
    });

    this.connection.on(SOCKET_EVENTS.AVATAR_INTERRUPTED, async (data: any) => {
      this.emit(SOCKET_EVENTS.AVATAR_INTERRUPTED, data);
    });

    this.connection.on(SOCKET_EVENTS.AVATAR_SPEAK, (data: any) => {
      this.emit(SOCKET_EVENTS.AVATAR_SPEAK, data);
    });

    this.connection.on(SOCKET_EVENTS.AVATAR_SPEAKING, (data: any) => {
      this.emit(SOCKET_EVENTS.AVATAR_SPEAKING, data);
    });

    this.connection.on(SOCKET_EVENTS.MESSAGE_RECEIVED, (data: any) => {
      this.emit(SOCKET_EVENTS.MESSAGE_RECEIVED, data);
    });

    this.connection.on(SOCKET_EVENTS.AUDIO_RECEIVED, (data: any) => {
      this.emit(SOCKET_EVENTS.AUDIO_RECEIVED, data);
    });

    this.connection.on(SOCKET_EVENTS.PROCESSING_STOPPED, (data: any) => {
      this.emit(SOCKET_EVENTS.PROCESSING_STOPPED, data);
    });

    this.connection.on(SOCKET_EVENTS.CONNECTION_ERROR, (data: any) => {
      this.emit(SOCKET_EVENTS.CONNECTION_ERROR, data);
    });

    this.connection.on(SOCKET_EVENTS.STREAMING_STARTED, (data: any) => {
      this.emit(SOCKET_EVENTS.STREAMING_STARTED, data);
    });

    this.connection.on(SOCKET_EVENTS.STREAMING_STOPPED, (data: any) => {
      this.emit(SOCKET_EVENTS.STREAMING_STOPPED, data);
    });

    this.connection.on(SOCKET_EVENTS.TRANSCRIPTION_INTERIM, (data: any) => {
      this.emit(SOCKET_EVENTS.TRANSCRIPTION_INTERIM, data);
    });

    this.connection.on(SOCKET_EVENTS.TRANSCRIPTION_FINAL, (data: any) => {
      this.emit(SOCKET_EVENTS.TRANSCRIPTION_FINAL, data);
    });

    this.connection.on(SOCKET_EVENTS.INTERVIEW_COMPLETED, (data: any) => {
      this.emit(SOCKET_EVENTS.INTERVIEW_COMPLETED, data);
    });
  }

  private eventListeners: { [key: string]: Array<(data: any) => void> } = {};

  private emit(event: string, data: any): void {
    const listeners = this.eventListeners[event] || [];
    listeners.forEach((listener) => listener(data));
  }

  on(event: string, callback: (data: any) => void): void {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  off(event: string, callback?: (data: any) => void): void {
    if (!this.eventListeners[event]) return;

    if (callback) {
      this.eventListeners[event] = this.eventListeners[event].filter(
        (cb) => cb !== callback
      );
    } else {
      delete this.eventListeners[event];
    }
  }

  private async startSession(
    templateId: string,
    userId: string,
    avatarSettings: AvatarSettings
  ): Promise<void> {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      const errorMsg = `SignalR connection not established. State: ${
        this.connection?.state || "null"
      }`;
      throw new Error(errorMsg);
    }

    try {
      // Call StartInterview with templateId, userId, and optional interviewerId
      // Note: templateId here is actually the interview ID from HR Portal
      // Backend will look up the actual template to use from the interview record
      // Use send() instead of invoke() since the backend sends events rather than returning a value
      await this.connection.send(
        SIGNALR_HUB_METHODS.START_SESSION,
        templateId,  // interview ID (backend will map to template)
        userId,      // userId parameter
        null         // interviewerId parameter (optional)
      );
    } catch (error) {
      throw error;
    }
  }

  async sendTextInput(message: string): Promise<void> {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      return;
    }

    try {
      await this.connection.invoke(
        SIGNALR_HUB_METHODS.SEND_TEXT_INPUT,
        message
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      if (
        errorMessage.includes("Invocation canceled") ||
        errorMessage.includes("connection being closed") ||
        errorMessage.includes("Connection is closed")
      ) {
        return;
      }

      throw error;
    }
  }

  async stopProcessing(): Promise<void> {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      return;
    }

    try {
      await this.connection.invoke(SIGNALR_HUB_METHODS.STOP_PROCESSING);
    } catch (error) {}
  }

  async startStreamingRecognition(): Promise<void> {
    debugLog("🎙️ [SignalR] startStreamingRecognition called");
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      const errorMsg = `SignalR connection not ready. State: ${
        this.connection?.state ?? "null"
      }`;
      debugError("❌ [SignalR] Cannot start recognition:", errorMsg);
      throw new Error(errorMsg);
    }

    try {
      this.hasLoggedFirstChunk = false;
      debugLog("📡 [SignalR] Invoking StartStreamingRecognition on hub...");
      await this.connection.invoke(
        SIGNALR_HUB_METHODS.START_STREAMING_RECOGNITION
      );
      debugLog("✅ [SignalR] StartStreamingRecognition invoked successfully");
    } catch (error) {
      debugError("❌ [SignalR] Error invoking StartStreamingRecognition:", error);
      throw error;
    }
  }

  async sendAudioChunk(audioData: ArrayBuffer): Promise<void> {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      return;
    }

    try {
      // Convert ArrayBuffer to base64 string for SignalR JSON protocol
      const uint8Array = new Uint8Array(audioData);
      let binary = '';
      const len = uint8Array.byteLength;
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(uint8Array[i]);
      }
      const base64String = btoa(binary);

      if (!this.hasLoggedFirstChunk) {
        debugLog("🎤 [SignalR] Sending first audio chunk, size:", audioData.byteLength, "bytes, base64 length:", base64String.length);
        this.hasLoggedFirstChunk = true;
      }

      await this.connection.invoke(
        SIGNALR_HUB_METHODS.SEND_AUDIO_CHUNK,
        base64String
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      debugError("❌ [SignalR] Error sending audio chunk:", errorMessage);
      if (
        !errorMessage.includes("Invocation canceled") &&
        !errorMessage.includes("connection") &&
        !errorMessage.includes("closed")
      ) {
      }
    }
  }

  async stopStreamingRecognition(): Promise<void> {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      return;
    }

    try {
      await this.connection.invoke(
        SIGNALR_HUB_METHODS.STOP_STREAMING_RECOGNITION
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      if (
        errorMessage.includes("Invocation canceled") ||
        errorMessage.includes("connection") ||
        errorMessage.includes("closed")
      ) {
      }
    }
  }

  async exitInterview(): Promise<void> {
    debugLog("🚪 [SignalR] exitInterview called");
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      const errorMsg = `SignalR connection not ready. State: ${
        this.connection?.state ?? "null"
      }`;
      debugError("❌ [SignalR] Cannot exit interview:", errorMsg);
      throw new Error(errorMsg);
    }

    try {
      debugLog("📡 [SignalR] Invoking ExitInterview on hub...");
      await this.connection.invoke(
        SIGNALR_HUB_METHODS.EXIT_INTERVIEW
      );
      debugLog("✅ [SignalR] ExitInterview invoked successfully");
    } catch (error) {
      debugError("❌ [SignalR] Error invoking ExitInterview:", error);
      throw error;
    }
  }

  async pauseSession(): Promise<void> {
    try {
      await this.stopProcessing();
      await this.stopStreamingRecognition();

      await azureCustomAvatarService.stopSpeaking();

      this.isPaused = true;
      this.emit(SOCKET_EVENTS.SESSION_PAUSED, { timestamp: new Date() });
    } catch (error) {}
  }

  async resumeSession(): Promise<void> {
    try {
      this.isPaused = false;
      this.emit(SOCKET_EVENTS.SESSION_RESUMED, { timestamp: new Date() });
    } catch (error) {}
  }

  async sendGreeting(): Promise<void> {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      debugWarn("Cannot send greeting - not connected");
      return;
    }

    try {
      await this.connection.send("SendGreeting");
      debugLog("Greeting request sent to backend");
    } catch (error) {
      debugError("Error sending greeting:", error);
    }
  }

  async notifyGreetingCompleted(): Promise<void> {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      debugWarn("Cannot notify greeting completed - not connected");
      return;
    }

    try {
      await this.connection.send("OnGreetingCompleted");
      debugLog("Notified backend that greeting completed");
    } catch (error) {
      debugError("Error notifying greeting completion:", error);
    }
  }

  async notifyProcessExplanationCompleted(): Promise<void> {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      debugWarn("Cannot notify processExplanation completed - not connected");
      return;
    }

    try {
      await this.connection.send("OnProcessExplanationCompleted");
      debugLog("✅ [SignalR] Notified backend that processExplanation completed - timer will start");
    } catch (error) {
      debugError("❌ [SignalR] Error notifying processExplanation completion:", error);
    }
  }

  async notifyNoResponsePromptCompleted(): Promise<void> {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      debugWarn("Cannot notify no-response prompt completed - not connected");
      return;
    }

    try {
      await this.connection.send("OnNoResponsePromptCompleted");
      debugLog("✅ [SignalR] Notified backend that no-response prompt completed - timer will reschedule");
    } catch (error) {
      debugError("❌ [SignalR] Error notifying no-response prompt completion:", error);
    }
  }

  async notifyTroubleshootingMessageCompleted(): Promise<void> {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      debugWarn("Cannot notify troubleshooting message completed - not connected");
      return;
    }

    try {
      await this.connection.send("OnTroubleshootingMessageCompleted");
      debugLog("✅ [SignalR] Notified backend that troubleshooting message completed - timer will reschedule");
    } catch (error) {
      debugError("❌ [SignalR] Error notifying troubleshooting message completion:", error);
    }
  }

  async notifyConfirmationCompleted(): Promise<void> {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      debugWarn("Cannot notify confirmation completed - not connected");
      return;
    }

    try {
      await this.connection.send("OnConfirmationCompleted");
      debugLog("Notified backend that confirmation prompt completed");
    } catch (error) {
      debugError("Error notifying confirmation completion:", error);
    }
  }

  async notifyClosingMessageCompleted(): Promise<void> {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      debugWarn("Cannot notify closing message completed - not connected");
      return;
    }

    try {
      await this.connection.send("OnClosingMessageCompleted");
      debugLog("Notified backend that closing message completed");
    } catch (error) {
      debugError("Error notifying closing message completion:", error);
    }
  }

  async notifyQuestionSpeechCompleted(): Promise<void> {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      debugWarn("Cannot notify question speech completed - not connected");
      return;
    }

    try {
      await this.connection.send("OnQuestionSpeechCompleted");
      debugLog("✅ [SignalR] Notified backend that question speech completed - timer will start");
    } catch (error) {
      debugError("❌ [SignalR] Error notifying question speech completion:", error);
    }
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        if (this.connection.state === "Disconnected") {
          this.connection = null;
          this.currentUserId = null;
          return;
        }

        this.intentionalDisconnect = true;

        await this.connection.stop();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        if (
          errorMessage.includes("Invocation canceled") ||
          errorMessage.includes("connection") ||
          errorMessage.includes("closed")
        ) {
        }
      } finally {
        this.connection = null;
        this.currentUserId = null;
      }
    }
  }

  private handleDisconnection(): void {
    const wasIntentional = this.intentionalDisconnect;
    const failedAfterRetries = this.reconnectAttempts > 0;

    this.currentSessionId = null;
    this.currentUserId = null;

    if (wasIntentional) {
      this.intentionalDisconnect = false;
      debugLog("🔌 [SignalR] Disconnected intentionally");
      this.emit(SOCKET_EVENTS.DISCONNECTED, { timestamp: new Date(), intentional: true });
    } else if (failedAfterRetries) {
      debugError(`❌ [SignalR] Disconnected after ${this.reconnectAttempts} reconnection attempts`);
      this.emit(SOCKET_EVENTS.MAX_RECONNECT_ATTEMPTS_REACHED, {
        attempts: this.reconnectAttempts,
        timestamp: new Date()
      });
      this.emit(SOCKET_EVENTS.DISCONNECTED, { timestamp: new Date(), intentional: false });
    } else {
      debugWarn("⚠️ [SignalR] Disconnected unexpectedly");
      this.emit(SOCKET_EVENTS.DISCONNECTED, { timestamp: new Date(), intentional: false });
    }

    this.reconnectAttempts = 0;
  }

  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  getConnectionState(): string {
    if (!this.connection) return "Disconnected";

    switch (this.connection.state) {
      case signalR.HubConnectionState.Connected:
        return "Connected";
      case signalR.HubConnectionState.Connecting:
        return "Connecting";
      case signalR.HubConnectionState.Disconnected:
        return "Disconnected";
      case signalR.HubConnectionState.Disconnecting:
        return "Disconnecting";
      case signalR.HubConnectionState.Reconnecting:
        return "Reconnecting";
      default:
        return "Unknown";
    }
  }
}

export const signalRService = new SignalRService();
